import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { BarChart } from '../components/charts/BarChart';
import { interpolateColors } from '../components/charts/colors';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

interface KpiItem {
  label: string;
  value: number;
  type: 'money' | 'count' | 'percent';
}

interface BreakdownItem {
  label: string;
  value: number;
}

interface MembershipsStats {
  kpis: KpiItem[];
  penalty_ageing: BreakdownItem[];
}

// Ordinal ramp for the penalty ageing chart — see Dashboard.tsx's original
// comment on this palette (kept identical for continuity).
const GREEN_RAMP_STOPS = ['#60aa45', '#3f7a29', '#2c591c'];

export function MembershipsDashboard() {
  const { hasPermission } = useAuth();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [stats, setStats] = useState<MembershipsStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<MembershipsStats>('/analytics/memberships/stats')
      .then(({ data }) => setStats(data))
      .catch((err: ApiError) => setError(err.message || 'Could not load the dashboard.'));
  }, []);

  const kpi = (label: string) => stats?.kpis.find((k) => k.label === label);
  const fmtKpi = (label: string) => {
    const item = kpi(label);
    if (!item) return '—';
    return item.type === 'money' ? fmtMoney(item.value) : item.value;
  };

  return (
    <>
      <header className="page-header">
        <h1>Memberships Dashboard</h1>
      </header>
      <p className="muted">A snapshot of members, employers, contributions, benefits, and penalties.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {!error && !stats && <p className="muted">Loading…</p>}

      {stats && (
        <>
          <div className="stat-grid">
            {['Total Members', 'Active Employers', 'Contributions Collected (This Month)', 'Benefits Disbursed (This Month)'].map(
              (label) => (
                <div className="card stat" key={label}>
                  <span className="stat-label">{label}</span>
                  <span className="stat-value">{fmtKpi(label)}</span>
                </div>
              )
            )}
          </div>

          <div className="chart-grid">
            <div className="card">
              <div className="chart-card-header">
                <h2>Penalties Outstanding</h2>
              </div>
              <div className="chart-card-hero">{fmtKpi('Penalties Outstanding')}</div>
              <BarChart
                data={stats.penalty_ageing.map((a) => ({ label: a.label, values: [a.value] }))}
                seriesColors={interpolateColors(GREEN_RAMP_STOPS, stats.penalty_ageing.length)}
                formatValue={fmtMoney}
              />
              {hasPermission('penalties.reports.view') && (
                <div className="chart-card-footer">
                  <Link to="/reports/penalties/ageing-schedule">View full report →</Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
